import { Link } from "react-router-dom";

export default function SEOContent() {
  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-gradient-to-b from-primary-800 to-primary-950">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-400/20 text-accent-200 text-xs font-label tracking-wider uppercase mb-4">
            SEO Resource
          </span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-white leading-tight max-w-2xl">
            Why GTBuy Sheet Is the Ultimate Tool for Smart Shopping
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16">
          <div className="space-y-4 text-white/75 text-sm md:text-base leading-relaxed">
            <p>
              In today&rsquo;s fast-paced online shopping landscape, finding the best price across
              multiple platforms can feel like a full-time job. That&rsquo;s where GTBuy Sheet comes
              in — a meticulously curated spreadsheet that brings together real-time pricing data
              from GTBuy, Kakobuy, and Superbuy into one clean, easy-to-navigate view.
            </p>
            <p>
              Unlike traditional browsing where you jump between tabs comparing prices manually, the
              GTBuy Sheet approach replaces scattered research with structured data comparison. Each
              product entry shows you exactly what you&rsquo;d pay on each platform, so you can spot
              the best deal in seconds rather than hours.
            </p>
            <p>
              The spreadsheet is updated daily with fresh curated deals across fourteen major
              categories — from shoes and hoodies to bags, belts, and perfumes. Whether you&rsquo;re
              a casual shopper looking for a single item or a reseller calculating margins on bulk
              orders, having all three platforms side by side eliminates guesswork and saves real
              money.
            </p>
          </div>
          <div className="space-y-4 text-white/75 text-sm md:text-base leading-relaxed">
            <p>
              What makes the system particularly powerful is its category-first organization. Rather
              than sifting through endless product listings, you jump directly to the category you
              care about — shoes, jackets, accessories — and immediately see the spread between
              GTBuy, Kakobuy, and Superbuy pricing. This structured approach means you spend less
              time searching and more time finding.
            </p>
            <p>
              For American and European shoppers, the spreadsheet addresses a common pain point:
              international platform pricing can vary dramatically for the same item. A pair of
              sneakers might be $85 on one platform and $62 on another — and without a side-by-side
              view, you&rsquo;d never know. GTBuy Sheet surfaces these discrepancies instantly.
            </p>
            <p>
              Ready to dive deeper into how spreadsheet shopping works and why it&rsquo;s
              transforming the way people buy fashion online? Check out our complete guide covering
              everything from getting started to advanced comparison strategies.
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8">
          <div className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20workspace%20with%20laptop%20showing%20spreadsheet%20on%20screen%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20modern%20aesthetic%2C%20coffee%20cup%20beside%20keyboard%2C%20editorial%20photography&width=400&height=400&seq=seo-content-thumb&orientation=squarish"
              alt="GTBuy Sheet guide"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="flex-1">
            <span className="text-3xl md:text-4xl font-heading font-bold text-white block mb-1">
              01
            </span>
            <p className="text-white/65 text-sm mb-3">
              The complete guide to spreadsheet shopping — strategies, tips, and how to get the most value
            </p>
            <Link
              to="/blog/gtbuy-spreadsheet-shopping-guide"
              className="inline-block whitespace-nowrap px-6 py-2.5 bg-white text-primary-800 font-label font-semibold text-sm rounded-full hover:bg-white/90 transition-all cursor-pointer"
            >
              Read Full Guide
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}